import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Divider,
  Typography,
  Grid,
} from '@mui/material';

import { ErrorOutline } from '@mui/icons-material';
import { useAppSelector } from '../../app/hooks';
import { ShareResults } from '../walls/types';

import { useUsers } from 'features/userManagement/api/getUsers';
import { useWeatherWalls } from './api/getWeatherWalls';
import { useShareWeatherWall } from './api/shareWeatherWall';

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedWallIds: number[];
}
const ShareWeatherWallsDialog = ({
  open,
  handleClose,
  selectedWallIds,
}: Props) => {
  const selfUserId = useAppSelector((state) => state.auth.user?.id);
  const { data: allWalls } = useWeatherWalls(selfUserId);

  const { data: users } = useUsers({});
  const shareWall = useShareWeatherWall({});

  const [userIdsForSharing, setUserIdsForSharing] = useState<number[]>([]);
  const [shareResultsByUserId, setShareResultsByUserId] =
    useState<ShareResults>({});
  const [shareErrorsByUserId, setShareErrorsByUserId] = useState<ShareResults>(
    {}
  );
  const [submitted, setSubmitted] = useState(false);

  const wallsToShare =
    allWalls?.filter((wall) => selectedWallIds.includes(wall.id)) || [];
  const usersNotSelf = users?.filter((user) => user.id !== selfUserId);

  const handleUserClicked = (userId: number) => {
    if (userIdsForSharing.includes(userId))
      setUserIdsForSharing(userIdsForSharing.filter((id) => id !== userId));
    else setUserIdsForSharing((priorIds) => [...priorIds, userId]);
  };

  const handleSubmit = async () => {
    const newShareResults: ShareResults = {};
    const newShareErrors: ShareResults = {};
    for await (const userId of userIdsForSharing) {
      for await (const wall of wallsToShare) {
        try {
          await shareWall.mutateAsync({
            wallId: wall.id,
            userId,
            saveOverWeatherWall: 0,
          });
          if (newShareResults[userId])
            newShareResults[userId] = [...newShareResults[userId], wall.name];
          else newShareResults[userId] = [wall.name];
        } catch (err) {
          if (newShareErrors[userId])
            newShareErrors[userId] = [...newShareErrors[userId], wall.name];
          else newShareErrors[userId] = [wall.name];
        }
      }
    }
    setShareResultsByUserId(newShareResults);
    setShareErrorsByUserId(newShareErrors);
    setSubmitted(true);
  };

  const handleCloseDialog = () => {
    handleClose();
    setSubmitted(false);
    setUserIdsForSharing([]);
    setShareResultsByUserId({});
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth='sm' fullWidth>
      <DialogTitle>Share Walls</DialogTitle>
      <DialogContent>
        <Grid container direction='column' spacing={2}>
          <Grid item>
            <Typography>
              Walls to Share: {wallsToShare.map((wall) => wall.name).join(', ')}
            </Typography>
          </Grid>
          <Grid item>
            <Divider />
            <List>
              <ListItem>Select Users to Share Walls With:</ListItem>
              {usersNotSelf?.map((user, idx) => (
                <ListItem key={idx}>
                  <ListItemIcon>
                    <Checkbox
                      checked={userIdsForSharing.includes(user.id)}
                      edge='start'
                      tabIndex={-1}
                      onClick={() => {
                        handleUserClicked(user.id);
                      }}
                      disabled={submitted}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={user.name}
                    secondary={
                      submitted && (
                        <>
                          {shareResultsByUserId[user.id]?.length > 0 && (
                            <>
                              <Typography
                                component='span'
                                variant='body2'
                                color='textPrimary'
                              >
                                Sharing Succeeded:{' '}
                              </Typography>
                              {shareResultsByUserId[user.id].join(', ') + ' '}
                            </>
                          )}
                          {shareErrorsByUserId[user.id]?.length > 0 && (
                            <>
                              <Typography
                                component='span'
                                variant='body2'
                                color='textPrimary'
                              >
                                Sharing Failed:{' '}
                              </Typography>
                              {shareErrorsByUserId[user.id].join(', ')}
                            </>
                          )}
                        </>
                      )
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Divider />
          </Grid>
          <Grid item container alignItems='center' spacing={1}>
            <Grid item>
              <ErrorOutline />
            </Grid>
            <Grid item style={{ marginBottom: '.5rem' }}>
              <Typography variant='subtitle2'>
                Sharing walls will fail if the target user already has a wall by
                the same name.
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      {!submitted && (
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant='contained'
            color='secondary'
          >
            Cancel
          </Button>
          <Button color='primary' variant='contained' onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      )}
      {submitted && (
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            variant='contained'
            color='primary'
          >
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ShareWeatherWallsDialog;
